import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Copy to the clipboard, with a moment of "Copied!" to say it worked.
 *
 * Where the clipboard is refused (an embedded browser, a denied permission),
 * the field holding the text is selected instead, so the user's own copy
 * shortcut does the rest.
 */
export function useCopy(fallback?: () => HTMLInputElement | null) {
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string) => {
      if (timer.current) window.clearTimeout(timer.current);
      try {
        await navigator.clipboard.writeText(text);
        setState('copied');
      } catch {
        const field = fallback?.();
        field?.focus();
        field?.select();
        setState('manual');
      }
      timer.current = window.setTimeout(() => setState('idle'), 2400);
    },
    [fallback],
  );

  return { copy, copied: state === 'copied', manual: state === 'manual' };
}
